import React, { ReactNode, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { LogoutOutlined } from '@ant-design/icons';
import { Avatar, Button, Dropdown, Tooltip } from 'antd';
import type { DropdownProps } from 'antd/lib/dropdown/dropdown.d';
import { formatTestId } from '@utils/test';
import { useAuthorization } from '@hooks/useAuthorization';
import { useStorageFeatureFlag } from '@hooks/useStorageFeatureFlag';
import { useUser } from '@hooks/useUser';
import { FeatureFlag } from '@constants/featureFlags';
import { USER_MENU_SETTINGS } from '@constants/user-menu';
import styles from './userMenu.module.scss';

const UserMenu = () => {
  const { user } = useUser();
  const { email, firstName, lastName, fullName, role } = user;
  const { hasPermission } = useAuthorization();
  const isRbacEnabled = useStorageFeatureFlag(FeatureFlag.RBAC);
  const [menuOpened, setMenuOpened] = useState<boolean>(false);

  const menuItems = useMemo(() => {
    const visibleMenuItems = USER_MENU_SETTINGS.filter((item) => {
      const hasLegacyRoleAccess = !item.userAccess || item.userAccess.includes(role);

      // TODO(RBAC): The hasLegacyRoleAccess check is a temporary measure until organization-level
      // permissions are fully implemented in the RBAC system. Once complete, this check
      // can be removed, and authorization will be handled solely by `hasPermission`.
      if (!isRbacEnabled) return hasLegacyRoleAccess;

      const hasRbacPermission = hasPermission(item.permissions || {});
      return hasRbacPermission && hasLegacyRoleAccess;
    });

    return visibleMenuItems.map((item) => {
      const ItemIcon = item.icon as ReactNode;
      return {
        label: item.link ? <Link to={item.link}> {item.label} </Link> : item.label,
        key: item.key,
        icon:
          item.key === 'logout' ? (
            <LogoutOutlined />
          ) : item.icon ? (
            <span className={styles.optionIcon}>{ItemIcon}</span>
          ) : null,
        'data-testid': formatTestId('userMenu', item.key),
      };
    });
  }, [user, hasPermission, isRbacEnabled]);

  const handleVisibleChange = (flag: boolean) => {
    setMenuOpened(flag);
  };

  const renderDropdownMenu = (menus: ReactNode) => (
    <>
      <div className={styles.userInfo}>
        <Avatar className={styles.userInfo_avatar}>
          {firstName[0]}
          {lastName[0]}
        </Avatar>
        <span className={styles.userInfo_details}>
          <span className={styles.userInfo_details_name}>{fullName}</span>
          <span className={styles.userInfo_details_email}>{email}</span>
        </span>
      </div>
      {menus}
    </>
  );

  const dropDownProps: DropdownProps = {
    menu: { items: menuItems },
    trigger: ['click'],
    open: menuOpened,
    onOpenChange: handleVisibleChange,
    popupRender: renderDropdownMenu,
    overlayClassName: styles.menuOverlay,
  };

  return (
    <Dropdown {...dropDownProps}>
      <Tooltip title={fullName}>
        <Button
          className={styles.menuButton}
          type="text"
          shape="circle"
          size="small"
          data-testid="userMenu_dropdownButton"
        >
          {firstName[0]}
          {lastName[0]}
        </Button>
      </Tooltip>
    </Dropdown>
  );
};

export default UserMenu;
